import { z } from "zod";

import * as DbEnum from "../../../generated/db-enum";
import { enumReadSchema } from "../../core/enum-read.schema";

/**
 * How hard the server-side ship gate applies — `OFF`, `WARN` or `ENFORCE`.
 *
 * Derived from the Prisma enum rather than restated, so the DB column and this
 * schema cannot drift. Same discipline as `ChatTicketStatusEnumSchema`.
 *
 * ## What the mode does and does not control
 *
 * WHICH files matter, and what "green" means for each, live once in
 * `VIBE_SHIP_GATE_ARTIFACTS` — the same declaration the bundled client hook is
 * proved equal to on every PR. The mode says only whether that declaration is
 * consulted and what a finding costs. Making the artifact list per-organization
 * would leave nothing for the parity proof to compare against, which is why the
 * tenant-facing knob is *how hard* and never *which files*.
 *
 * ## Why three states rather than two
 *
 * `OFF` and `ENFORCE` are exactly the old boolean's `false` and `true`. `WARN`
 * is the on-ramp, and it exists for a specific population: Vibe's builders are
 * explicitly non-technical, and handing one a hard refusal the first time they
 * meet a rule they have never seen is how an opt-in safety feature gets switched
 * off org-wide and never switched back on. `WARN` makes the rule visible before
 * it is binding, and it lets an org turn `ENFORCE` on with evidence about how
 * often it would have fired.
 */
export const VIBE_SHIP_GATE_MODES = DbEnum.VIBE_SHIP_GATE_MODE_VALUES;

export type VibeShipGateMode = (typeof VIBE_SHIP_GATE_MODES)[number];

export const VibeShipGateModeSchema = z.enum(VIBE_SHIP_GATE_MODES);

/** `VibeShipGateModeSchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeShipGateModeReadSchema = enumReadSchema(VIBE_SHIP_GATE_MODES);

/**
 * The mode an app, a deployment, or an organization that has configured nothing
 * carries. Exported so no caller writes the literal — a default restated at a
 * call site is a default that drifts from the DB's own.
 */
export const VIBE_SHIP_GATE_MODE_DEFAULT: VibeShipGateMode = "OFF";

/**
 * The modes under which the gate READS the repository.
 *
 * `OFF` is excluded, and that is the whole cost model: no git project lookup, no
 * credential resolution, and none of the five blob reads happen for an app that
 * has not opted in. An org that has never configured anything pays nothing and
 * behaves exactly as it did before the gate existed.
 */
export const VIBE_SHIP_GATE_EVALUATED_MODES = [
  "WARN",
  "ENFORCE"
] as const satisfies readonly VibeShipGateMode[];

export type VibeShipGateEvaluatedMode = (typeof VIBE_SHIP_GATE_EVALUATED_MODES)[number];

/**
 * The modes under which a finding REFUSES the deploy.
 *
 * A strict subset of the evaluated modes: `WARN` reads and reports, `ENFORCE`
 * reads and refuses. Nothing may block without first having looked, which the
 * `satisfies` below makes a compile-time fact rather than a convention.
 */
export const VIBE_SHIP_GATE_BLOCKING_MODES = [
  "ENFORCE"
] as const satisfies readonly VibeShipGateEvaluatedMode[];

export type VibeShipGateBlockingMode = (typeof VIBE_SHIP_GATE_BLOCKING_MODES)[number];

/**
 * Compile-time exhaustiveness check. Every mode must be EITHER evaluated above
 * OR explicitly named here as one the gate does not read for. A fourth mode
 * added without deciding which it is fails the `never` constraint, and the only
 * ways out are to add it to the evaluated tuple or to name it in the `Exclude`
 * clause — so a new mode cannot arrive with undefined gate behaviour.
 *
 * Modelled on `VIBE_GIT_PUSH_NO_DEPLOY_REASONS`, which forces every no-deploy
 * reason to be classified as audit-worthy or as ordinary traffic. That enum is
 * NOT reused here and must not be: it answers "a push advanced a ref and built
 * nothing", and a warned or refused deployment has a deployment row. Filing one
 * deploy outcome in two ledgers with two vocabularies is the split this avoids
 * — the mechanism is worth copying, the enum is not worth borrowing.
 */
type _UnclassifiedShipGateMode = Exclude<VibeShipGateMode, VibeShipGateEvaluatedMode | "OFF">;
const _shipGateModeCheck: _UnclassifiedShipGateMode extends never ? true : never = true;
void _shipGateModeCheck;

/**
 * True when this mode makes the gate read the repository. Narrows, so a caller
 * can hand the mode straight on without re-asserting.
 */
export function isVibeShipGateEvaluated(mode: VibeShipGateMode): mode is VibeShipGateEvaluatedMode {
  return (VIBE_SHIP_GATE_EVALUATED_MODES as readonly string[]).includes(mode);
}

/**
 * True when a finding under this mode refuses the deploy.
 *
 * Read this, never `mode === "ENFORCE"`: the comparison is correct today and
 * silently wrong the moment a second blocking mode exists, whereas this function
 * and the tuple above move together.
 */
export function isVibeShipGateBlocking(mode: VibeShipGateMode): mode is VibeShipGateBlockingMode {
  return (VIBE_SHIP_GATE_BLOCKING_MODES as readonly string[]).includes(mode);
}

/**
 * The mode as the pre-mode boolean `requireVerification` saw it.
 *
 * A compatibility projection for clients that predate the mode — a published CLI
 * reads this field off the app view and renders it in a table. `WARN` projects
 * to `false`, which is the honest answer to the only question a boolean can ask:
 * a warning app does not refuse. It is a lossy READ, never a source of truth;
 * the deploy path reads the mode.
 */
export function vibeShipGateModeRefusesDeploys(mode: VibeShipGateMode): boolean {
  return isVibeShipGateBlocking(mode);
}

/**
 * The boolean `requireVerification` as a mode, for the same clients writing
 * rather than reading. `true` means what it always meant.
 *
 * A client that sends BOTH the boolean and the mode has sent a contradiction it
 * authored; the richer field wins, and the caller resolving the two says so at
 * its own site.
 */
export function vibeShipGateModeFromRequireVerification(
  requireVerification: boolean
): VibeShipGateMode {
  return requireVerification ? "ENFORCE" : "OFF";
}
